<template>
  <div class="bg-white min-vh-100 p-4 container mt-4">
    <!-- Import Progress Overlay -->
    <div v-if="importProgress.show" class="import-progress-overlay">
      <div class="import-progress-modal">
        <div class="text-center mb-3">
          <h5>Đang import file...</h5>
          <p class="text-muted mb-2">{{ importProgress.fileName }}</p>
        </div>
        
        <div class="progress mb-3" style="height: 25px;">
          <div 
            class="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
            role="progressbar" 
            :style="{ width: importProgress.percent + '%' }"
            :aria-valuenow="importProgress.percent" 
            aria-valuemin="0" 
            aria-valuemax="100"
          >
            {{ importProgress.percent.toFixed(1) }}%
          </div>
        </div>
        
        <div class="text-center text-muted">
          <small>
            {{ importProgress.currentCount.toLocaleString() }} / {{ importProgress.totalRecords.toLocaleString() }} số điện thoại
          </small>
        </div>
      </div>
    </div>

    <!-- Nếu đang xem danh sách -->
    <div v-if="!showDetail">
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <a class="nav-link" :class="{ active: activeTab === 'files' }" href="#" @click.prevent="activeTab = 'files'">
            Danh sách file
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link" :class="{ active: activeTab === 'search' }" href="#" @click.prevent="switchToSearchTab()">
            Tra cứu STB
          </a>
        </li>
      </ul>

      <!-- Tab: Danh sách file -->
      <div v-if="activeTab === 'files'">
        <!-- Import file button -->
        <div class="row mb-3">
          <div class="col-12 d-flex justify-content-end gap-2">
            <button class="btn btn-danger" @click="showImportModal = true" :disabled="importProgress.show">
              <span v-if="importProgress.show" class="spinner-border spinner-border-sm me-2"></span>
              {{ importProgress.show ? 'Đang import...' : 'Import file' }}
            </button>
          </div>
        </div>

      <!-- Danh sách file -->
      <div class="row">
        <div class="col-12">
          <div class="table-responsive bg-white shadow-sm rounded">
            <table class="table table-bordered table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="text-center" style="width: 60px;">STT</th>
                  <th scope="col" class="text-center" style="width: 260px;">File</th>
                  <th scope="col" class="text-center" style="width: 160px;">Người import</th>
                  <th scope="col" class="text-center" style="width: 180px;">Ngày bắt đầu</th>
                  <th scope="col" class="text-center" style="width: 120px;">Số lượng</th>
                  <th scope="col" class="text-center" style="width: 180px;">Ngày import</th>
                  <th scope="col" class="text-center" style="width: 180px;">Trạng thái</th>
                  <th scope="col" class="text-center" style="width: 180px;">Ghi chú</th>
                  <th scope="col" class="text-center" style="width: 200px;">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="9" class="text-center">
                    <span class="spinner-border spinner-border-sm me-2"></span>Đang tải...
                  </td>
                </tr>
                <tr v-else-if="fileData.length == 0">
                  <td colspan="9" class="text-center text-muted">Không tìm thấy file</td>
                </tr>
                <tr v-for="(item, index) in fileData" :key="index" v-else>
                  <td class="text-center">{{ (currentPage - 1) * perPage + index + 1 }}</td>
                  <td class="text-center">{{ item.file_name }}</td>
                  <td class="text-center">{{ item.import_by || '-' }}</td>
                  <td class="text-center">{{ formatDate(item.run_date) }}</td>
                  <td class="text-center">{{ item.count }}</td>
                  <td class="text-center">{{ formatDate(item.import_date) }}</td>
                  <td class="text-center">{{ item.status }}</td>
                  <td class="text-center">
                    <span
                      v-if="item.note"
                      class="note-link"
                      @click="showNoteModal(item.note)"
                      :title="item.note"
                    >
                      {{ truncateNote(item.note) }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-center action-cell">
                    <div class="action-links">
                      <span class="action-link" @click="openDetail(item)">
                        Chi tiết
                      </span>
                      <span v-if="item.status === 'Đang thực hiện' || item.status === 'Chưa bắt đầu'"
                        class="action-link" @click="activeFileImport('Stop', item)"
                        :class="{ 'disabled': actionLoadingFileId === item.file_id }">
                        <span v-if="actionLoadingFileId === item.file_id" class="spinner-border spinner-border-sm me-1"></span>
                        {{ actionLoadingFileId === item.file_id ? 'Đang xử lý...' : 'Dừng' }}
                      </span>
                      <!-- <span v-else-if="item.status === 'Tạm dừng'" class="action-link"
                        @click="activeFileImport('Resume', item)">
                        Chạy lại
                      </span> -->
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-3 px-2">
            <div class="text-muted">
              Hiển thị {{ fileData.length }} / {{ totalItems }} file
            </div>
            <nav v-if="totalPages > 1">
              <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(1)">&laquo;</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">&lsaquo;</a>
                </li>
                <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">&rsaquo;</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(totalPages)">&raquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      </div>

      <!-- Tab: Tra cứu STB -->
      <div v-if="activeTab === 'search'">
        <!-- Search filters -->
        <div class="row mb-3">
          <div class="col-4">
            <input 
              type="search" 
              v-model="searchAllPhone" 
              class="form-control" 
              placeholder="Nhập số thuê bao cần tìm..."
              @keyup.enter="searchAllPhones"
            />
          </div>
          <div class="col-3">
            <select v-model="searchStatus" class="form-control custom-select">
              <option value="">-- Tất cả trạng thái --</option>
              <option value="PENDING">Chờ xử lý</option>
              <option value="LOCK_1C">Khóa 1 chiều</option>
              <option value="LOCK_2C">Khóa 2 chiều</option>
              <option value="REPROCESS">Thu hồi</option>
              <option value="UPDATED">Đã cập nhật</option>
              <option value="SKIP">Bỏ qua</option>
              <option value="STOP">Đã dừng</option>
            </select>
          </div>
          <div class="col-2">
            <button class="btn btn-primary w-100" @click="searchAllPhones">Tìm kiếm</button>
          </div>
        </div>

        <!-- Results table -->
        <div class="table-responsive bg-white shadow-sm rounded">
          <table class="table table-bordered table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th class="text-center" style="width: 60px;">STT</th>
                <th class="text-center" style="width: 150px;">STB</th>
                <th class="text-center" style="width: 120px;">Trạng thái</th>
                <th class="text-center" style="width: 120px;">Người import</th>
                <th class="text-center" style="width: 120px;">Ngày import</th>
                <th class="text-center" style="width: 120px;">Ngày bắt đầu</th>
                <th class="text-center" style="width: 150px;">File</th>
                <th class="text-center" style="width: 100px;">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="searchLoading">
                <td colspan="8" class="text-center">
                  <span class="spinner-border spinner-border-sm me-2"></span>Đang tải...
                </td>
              </tr>
              <tr v-else-if="searchResults.length === 0">
                <td colspan="8" class="text-center text-muted">Không tìm thấy kết quả</td>
              </tr>
              <tr v-for="(item, index) in searchResults" :key="item.id">
                <td class="text-center">{{ (searchCurrentPage - 1) * searchPerPage + index + 1 }}</td>
                <td class="text-center">{{ formatPhoneNumber(item.sdt) }}</td>
                <td class="text-center">
                  <span :class="getStatusBadgeClass(item.status)">{{ getStatusText(item.status) }}</span>
                </td>
                <td class="text-center">{{ item.import_by || '-' }}</td>
                <td class="text-center">{{ formatDate(item.import_date) }}</td>
                <td class="text-center">{{ formatDate(item.run_date) }}</td>
                <td class="text-center">{{ item.file_name }}</td>
                <td class="text-center">
                  <div class="action-links">
                    <span class="action-link" @click="viewPhoneHistory(item)">Lịch sử</span>
                    <span v-if="item.active !== false && item.status !== 'UPDATED' && item.status !== 'SKIP' && item.status !== 'REPROCESS' && item.status !== 'ERROR' && item.status !== 'STOP'"
                      class="action-link text-danger"
                      @click="stopSinglePhone(item)"
                      :class="{ 'disabled': stopLoadingPhoneId === item.id }">
                      <span v-if="stopLoadingPhoneId === item.id" class="spinner-border spinner-border-sm me-1"></span>
                      {{ stopLoadingPhoneId === item.id ? 'Đang xử lý...' : 'Dừng' }}
                    </span>
                    <span v-if="item.status === 'STOP'" class="text-muted">
                      Số đã dừng
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="searchResults.length > 0">
          <div class="text-muted">
            Hiển thị {{ (searchCurrentPage - 1) * searchPerPage + 1 }} - {{ Math.min(searchCurrentPage * searchPerPage, searchTotal) }} / {{ searchTotal }} kết quả
          </div>
          <nav>
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: searchCurrentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="goToSearchPage(1)">&laquo;</a>
              </li>
              <li class="page-item" :class="{ disabled: searchCurrentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="goToSearchPage(searchCurrentPage - 1)">&lsaquo;</a>
              </li>
              <li v-for="page in visibleSearchPages" :key="page" class="page-item" :class="{ active: page === searchCurrentPage }">
                <a class="page-link" href="#" @click.prevent="goToSearchPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" :class="{ disabled: searchCurrentPage === searchTotalPages }">
                <a class="page-link" href="#" @click.prevent="goToSearchPage(searchCurrentPage + 1)">&rsaquo;</a>
              </li>
              <li class="page-item" :class="{ disabled: searchCurrentPage === searchTotalPages }">
                <a class="page-link" href="#" @click.prevent="goToSearchPage(searchTotalPages)">&raquo;</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Nếu đang xem chi tiết -->
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5>Chi tiết: {{ selectedFile }}</h5>
        <button class="btn btn-secondary" @click="backToList">
          ← Quay lại danh sách
        </button>
      </div>

      <!-- Ô tìm kiếm số điện thoại -->
      <div class="row mb-3">
        <div class="col-6">
          <input type="search" v-model="searchPhone" class="form-control" placeholder="Nhập số thuê bao cần tìm..."
            @search="resetSearch" />
        </div>
        <div class="col-2">
          <button class="btn btn-primary w-100" @click="searchPhoneNumber">Tìm kiếm</button>
        </div>
      </div>

      <!-- Bảng chi tiết -->
      <div class="table-responsive bg-white shadow-sm rounded">
        <table class="table table-bordered table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th class="text-center" style="width: 60px;">STT</th>
              <th class="text-center" style="width: 200px;">Số thuê bao</th>
              <th class="text-center" style="width: 200px;">Trạng thái</th>
              <th class="text-center" style="width: 100px;">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="4" class="text-center">
                <span class="spinner-border spinner-border-sm me-2"></span>Đang tải...
              </td>
            </tr>
            <tr v-else-if="phoneData.length === 0">
              <td colspan="4" class="text-center text-muted">Không tìm thấy số điện thoại</td>
            </tr>
            <tr v-for="(row, index) in phoneData" :key="index" v-else>
              <td class="text-center">{{ (phoneCurrentPage - 1) * phonePerPage + index + 1 }}</td>
              <td class="text-center">{{ row.sdt }}</td>
              <td class="text-center">{{ formatStatus(row.status) }}</td>
              <td class="text-center">
                <div class="action-links">
                  <span class="action-link" @click="showHistory(row)">Lịch sử</span>
                  <span v-if="selectedFileStatus !== 'Tạm dừng' && selectedFileStatus !== 'Đã hoàn thành' && row.status !== 'UPDATED' && row.status !== 'SKIP' && row.status !== 'REPROCESS' && row.status !== 'ERROR' && row.status !== 'STOP'"
                    class="action-link text-danger"
                    @click="stopDetailPhone(row)"
                    :class="{ 'disabled': stopLoadingPhoneId === row.id }">
                    <span v-if="stopLoadingPhoneId === row.id" class="spinner-border spinner-border-sm me-1"></span>
                    {{ stopLoadingPhoneId === row.id ? 'Đang xử lý...' : 'Dừng' }}
                  </span>
                  <span v-if="row.status === 'STOP'" class="text-muted">Số đã dừng</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Phone Pagination -->
      <div class="d-flex justify-content-between align-items-center mt-3 px-2">
        <div class="text-muted">
          Hiển thị {{ phoneData.length }} / {{ phoneTotalItems }} số điện thoại (Trang {{ phoneCurrentPage }}/{{ phoneTotalPages }})
        </div>
        <nav v-if="phoneTotalPages > 1">
          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: phoneCurrentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPhonePage(1)">&laquo;</a>
            </li>
            <li class="page-item" :class="{ disabled: phoneCurrentPage === 1 }">
              <a class="page-link" href="#" @click.prevent="goToPhonePage(phoneCurrentPage - 1)">&lsaquo;</a>
            </li>
            <li v-for="page in visiblePhonePages" :key="page" class="page-item" :class="{ active: page === phoneCurrentPage }">
              <a class="page-link" href="#" @click.prevent="goToPhonePage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: phoneCurrentPage === phoneTotalPages }">
              <a class="page-link" href="#" @click.prevent="goToPhonePage(phoneCurrentPage + 1)">&rsaquo;</a>
            </li>
            <li class="page-item" :class="{ disabled: phoneCurrentPage === phoneTotalPages }">
              <a class="page-link" href="#" @click.prevent="goToPhonePage(phoneTotalPages)">&raquo;</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Import Modal -->
    <div v-if="showImportModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h5 class="modal-title mb-0">Import file</h5>
          <button type="button" class="btn-close" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Date Input -->
          <div class="mb-3">
            <label class="form-label">Chọn thời gian bắt đầu</label>
            <div class="input-group position-relative">
              <!-- Input hiển thị dd/mm/yyyy -->
              <input type="text" class="form-control" v-model="formattedStartDate" @input="handleDateInput"
                @focus="showDatePicker" placeholder="dd/mm/yyyy" pattern="\d{2}/\d{2}/\d{4}" maxlength="10" readonly>
              <!-- Input date ẩn để có calendar -->
              <input type="date" ref="hiddenDateInput" class="position-absolute"
                style="opacity: 0; pointer-events: none; z-index: -1;" v-model="importForm.startDate"
                @change="handleDateChange" :min="todayFormatted">
              <span class="input-group-text" @click="openCalendar" style="cursor: pointer;">
                <i class="fas fa-calendar-alt"></i>
              </span>
            </div>
          </div>

          <!-- File Upload Buttons -->
          <div class="mb-3">
            <label class="form-label">File import</label>
            <div class="upload-container">
              <input type="file" ref="fileInput" @change="handleFileSelect" accept=".xlsx,.xls,.csv"
                style="display: none;">
              <button class="btn btn-outline-secondary w-100 upload-btn" :class="{ 'has-file': uploadedFile }"
                @click="triggerFileInput">
                <i v-if="!uploadedFile" class="fas fa-upload"></i>
                <span class="file-name-text">{{ uploadedFileName || 'Upload file' }}</span>
              </button>
              <button v-if="uploadedFile" class="btn-clear-file" @click="clearFile" title="Xóa file">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <p class="text-danger pt-1 pl-1 mb-0" v-if="!uploadedFile">Vui lòng chọn file</p>

            <!-- Download Template Button -->
            <button class="btn btn-outline-secondary w-100 py-2 mt-2" @click="downloadTemplate">
              <i class="fas fa-download me-2"></i>
              Tải file mẫu
            </button>
          </div>

          <!-- Notes -->
          <div class="mb-3">
            <label class="form-label">Ghi chú</label>
            <textarea class="form-control" rows="3" v-model="importForm.notes" placeholder="Nhập ghi chú..."></textarea>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-danger px-4" @click="handleImport" :disabled="loading || !uploadedFile">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ loading ? 'Đang tải...' : 'Import' }}
          </button>
        </div>
      </div>
    </div>

    <!-- History Modal -->
    <div v-if="showHistoryModal" class="modal-overlay" @click="closeHistoryModal">
      <div class="modal-content history-modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h5 class="modal-title mb-0">Lịch sử xử lý: {{ selectedPhoneNumber }}</h5>
          <button type="button" class="btn-close" @click="closeHistoryModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div v-if="loadingHistory" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Đang tải...</span>
            </div>
            <p class="mt-2">Đang tải lịch sử...</p>
          </div>

          <div v-else-if="historyError" class="alert alert-danger">
            {{ historyError }}
          </div>

          <div v-else-if="historyData.length === 0" class="text-center py-4 text-muted">
            <i class="fas fa-history fa-2x mb-2"></i>
            <p>Chưa có lịch sử xử lý nào</p>
          </div>

          <div v-else class="history-container">
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th class="text-center" style="width: 160px;">Thời gian</th>
                    <th class="text-center" style="width: 100px;">Loại</th>
                    <th class="text-center" style="width: 120px;">Trạng thái</th>
                    <th class="text-center">Nội dung SMS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in historyData" :key="index">
                    <td class="text-center">{{ formatDateTime(item.action_time) }}</td>
                    <td class="text-center">
                      <span class="status-badge" :class="getTypeBadgeClass(item.type)">
                        {{ formatType(item.type) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="status-badge" :class="getStatusClass(item.status)">
                        {{ getVietnameseStatus(item.status) }}
                      </span>
                    </td>
                    <td class="text-left">
                      <span v-if="item.sms_content && item.sms_content.trim()"
                            class="sms-content-truncate" @click="showContentDetail(item)">
                        {{ truncateText(item.sms_content, 80) }}
                        <i class="fas fa-external-link-alt ms-1 text-primary"></i>
                      </span>
                      <span v-else-if="isSMSType(item.type)" class="text-muted">Không có nội dung</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeHistoryModal">
            Đóng
          </button>
        </div>
      </div>
    </div>

    <!-- Content Detail Modal -->
    <div v-if="showContentDetailModal" class="modal-overlay" @click="closeContentDetailModal">
      <div class="modal-content content-detail-modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h5 class="modal-title mb-0">{{ contentDetailTitle }}</h5>
          <button type="button" class="btn-close" @click="closeContentDetailModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div class="content-detail-info mb-3">
            <div class="row">
              <div class="col-md-3">
                <strong>Thời gian:</strong>
              </div>
              <div class="col-md-9">
                {{ formatDateTime(contentDetailData.action_time) }}
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-md-3">
                <strong>Loại:</strong>
              </div>
              <div class="col-md-9">
                <span class="badge" :class="getTypeBadgeClass(contentDetailData.type)">
                  {{ formatType(contentDetailData.type) }}
                </span>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-md-3">
                <strong>Trạng thái:</strong>
              </div>
              <div class="col-md-9">
                <span class="status-badge" :class="getStatusClass(contentDetailData.status)">
                  {{ getVietnameseStatus(contentDetailData.status) }}
                </span>
              </div>
            </div>
          </div>

          <!-- SMS Content Section -->
          <div v-if="contentDetailData.sms_content && contentDetailData.sms_content.trim()" class="content-section mb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="mb-0"><strong>Nội dung SMS:</strong></h6>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="copySmsContent"
                title="Sao chép nội dung SMS">
                <i class="fas fa-copy me-1"></i>
                Copy SMS
              </button>
            </div>
            <div class="content-display sms-display">
              {{ contentDetailData.sms_content || 'Không có nội dung SMS' }}
            </div>
          </div>

          </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeContentDetailModal">
            Đóng
          </button>
        </div>
      </div>
    </div>

    <!-- Note Detail Modal -->
    <div v-if="showNoteDetailModal" class="modal-overlay" @click="closeNoteModal">
      <div class="modal-content" @click.stop style="max-width: 500px;">
        <div class="modal-header d-flex justify-content-between align-items-center">
          <h5 class="modal-title mb-0">Ghi chú</h5>
          <button type="button" class="btn-close" @click="closeNoteModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="note-content" style="white-space: pre-wrap; word-break: break-word;">
            {{ noteDetailContent }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeNoteModal">
            Đóng
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import useJwt from "@/auth/jwt/useJwt";
import { $ctx } from "@themeConfig";
import * as XLSX from 'xlsx';
export default {
  data() {
    return {
      showImportModal: false,
      showDetail: false,
      selectedFile: null,
      selectedFileStatus: null,
      selectedImportBy: null,
      selectedImportDate: null,
      selectedRunDate: null,
      detailData: [],
      filteredDetailData: [],
      searchPhone: '',
      loading: false,
      error: null,
      importForm: {
        startDate: ''
      },
      uploadedFile: null,
      uploadedFileName: '',
      fileReading: false, // Loading state when reading file
      actionLoadingFileId: null, // Track which file is being stopped/resumed
      phoneNumbersList: [], // Danh sách số điện thoại từ file
      fileData: [{
        stt: 0,
        file_name: '',
        import_by: '',
        run_date: '',
        count: 0,
        import_date: '',
        status: ''
      }],
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      totalPages: 0,
      phoneData: [
        {
          sdt: '',
          status: ''
        }
      ],
      originalPhoneData: [], // Lưu dữ liệu gốc để có thể reset khi cần
      // Phone detail pagination
      phoneCurrentPage: 1,
      phonePerPage: 50,
      phoneTotalItems: 0,
      phoneTotalPages: 0,
      selectedFileId: null,
      // Import progress polling
      importPollingInterval: null,
      importProgress: {
        show: false,
        fileId: null,
        fileName: '',
        totalRecords: 0,
        currentCount: 0,
        percent: 0
      },
      // History modal
      showHistoryModal: false,
      selectedPhoneNumber: '',
      historyData: [],
      loadingHistory: false,
      historyError: null,

      // Content detail modal
      showContentDetailModal: false,
      contentDetailData: {},
      contentDetailTitle: '',

      // Note modal
      showNoteDetailModal: false,
      noteDetailContent: '',

      // Search all phones tab
      activeTab: 'files',
      searchAllPhone: '',
      searchStatus: '',
      searchResults: [],
      searchLoading: false,
      searchCurrentPage: 1,
      searchPerPage: 20,
      searchTotal: 0,
      searchTotalPages: 0,
      stopLoadingPhoneId: null
    }
  },
  computed: {
    todayFormatted() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formattedStartDate: {
      get() {
        if (!this.importForm.startDate) return '';
        const date = new Date(this.importForm.startDate);
        if (isNaN(date.getTime())) return '';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      },

      set(value) {
        if (!value) {
          this.importForm.startDate = '';
          return;
        }

        const parts = value.split('/');
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0');
          const month = parts[1].padStart(2, '0');
          const year = parts[2];
          this.importForm.startDate = `${year}-${month}-${day}`;
        }
      }
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    visiblePhonePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.phoneCurrentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.phoneTotalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    visibleSearchPages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.searchCurrentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.searchTotalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mounted() {
    this.fetchFiles()
  },
  beforeDestroy() {
    this.stopImportPolling();
  },
  methods: {
    async activeFileImport(action, item) {
      // Prevent double click while loading
      if (this.actionLoadingFileId === item.file_id) {
        return;
      }

      // Xác định text cho popup xác nhận
      const actionText = action === 'Stop' ? 'dừng' : 'chạy lại';
      const confirmMessage = `Bạn có chắc chắn muốn ${actionText} file "${item.file_name}" không?`;

      // Hiển thị popup xác nhận
      if (!confirm(confirmMessage)) {
        return; // Nếu user chọn Cancel thì không thực hiện gì
      }

      // Stop polling if stopping a file that's being imported
      if (action === 'Stop') {
        // Always stop polling when stopping any file (in case file_id doesn't match due to timing)
        this.stopImportPolling();
        this.importProgress.show = false;
      }

      this.actionLoadingFileId = item.file_id; // Set loading for this specific file
      this.error = null;
      try {
        const res = await useJwt.put("p/transactions/activeFile", {
          file_id: item.file_id,
          file_name: item.file_name,
          import_date: item.import_date,
          run_date: item.run_date,
          action: action,
          username: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'unknown',
        });
        console.log("API request:", {
          file_name: item.file_name,
          import_date: item.import_date,
          run_date: item.run_date,
          action: action,
          username: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'unknown',
        });
        console.log("API response:", res); // log toàn bộ response

        if (res.data && res.data.code === 200) {
          const result = res.data.result;

          // Backend trả về object với code 400 nếu không tìm thấy file
          if (typeof result === 'object' && result.code === 400) {
            this.error = result.str || `Không tìm thấy file.`;
            alert(this.error);
          }
          // Backend trả về string message khi thành công
          else if (typeof result === 'string') {
            alert(result); // Hiển thị message từ backend: "File đã được dừng thành công." hoặc "File đã được tiếp tục thành công."
            await this.fetchFiles(); // Refresh danh sách file
          }
          else {
            // Trường hợp không xác định
            const successText = action === 'Stop' ? 'Dừng' : 'Chạy lại';
            alert(`${successText} file thành công!`);
            await this.fetchFiles();
          }
        } else {
          console.error("API response error:", res.data);
          this.error = res.data.message || `API trả về lỗi khi ${actionText} file.`;
          alert(this.error);
        }

      } catch (err) {
        console.error("API error:", err); // log lỗi chi tiết

        // Xử lý lỗi từ backend (string message)
        if (err.response && err.response.data && typeof err.response.data.result === 'string') {
          this.error = err.response.data.result;
          alert(this.error);
        } else {
          this.error = `Không thể ${actionText} file.`;
          alert(this.error);
        }
      } finally {
        this.actionLoadingFileId = null; // Clear loading state
      }
    },

    async fetchFiles() {
      this.loading = true;
      this.error = null;
      try {
        const res = await useJwt.get("p/transactions/get_all_file", {
          params: { page: this.currentPage, per_page: this.perPage }
        });
        console.log("API response:", res);

        if (res.data && res.data.code === 200) {
          const data = res.data.result;
          console.log("API data:", data);

          if (data && Array.isArray(data.items)) {
            this.fileData = data.items;
            this.totalItems = data.total;
            this.totalPages = data.total_pages;
            this.currentPage = data.page;
            console.log("Updated fileData:", this.fileData);
          } else {
            console.error("Result is not valid:", data);
            this.fileData = [];
            this.error = "Dữ liệu trả về không đúng định dạng.";
          }
        } else {
          console.error("API response error:", res.data);
          this.error = res.data.message || "API trả về lỗi.";
          this.fileData = [];
        }

      } catch (err) {
        console.error("API error:", err); // log lỗi chi tiết
        this.error = "Không thể tải danh sách file.";
        this.fileData = []; // Reset về mảng rỗng khi có lỗi
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchFiles();
      }
    },

    async fetchFileDetails(file_id, phone = '') {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          file_id,
          page: this.phoneCurrentPage,
          per_page: this.phonePerPage
        };

        if (phone) {
          params.phone = phone;
        }

        const res = await useJwt.get("p/transactions/get_phone", {
          params
        });
        console.log("API response:", res);

        if (res.data && res.data.code === 200) {
          const data = res.data.result;
          console.log("API data:", data);

          if (data && Array.isArray(data.items)) {
            this.phoneData = data.items;
            this.originalPhoneData = [...data.items];
            this.phoneTotalItems = data.total;
            this.phoneTotalPages = data.total_pages;
            this.phoneCurrentPage = data.page;

            const firstRow = data.items[0];
            if (firstRow) {
              this.selectedImportBy = firstRow.import_by || this.selectedImportBy;
              this.selectedImportDate = firstRow.import_date || this.selectedImportDate;
              this.selectedRunDate = firstRow.run_date || this.selectedRunDate;
            }

            console.log("Updated phoneData:", this.phoneData);
          } else {
            console.error("Result is not valid:", data);
            this.phoneData = [];
            this.originalPhoneData = [];
            this.error = "Dữ liệu số điện thoại không đúng định dạng.";
          }
        } else {
          console.error("API response error:", res.data);
          this.error = res.data.message || "API trả về lỗi khi lấy chi tiết file.";
          this.phoneData = [];
          this.originalPhoneData = [];
        }

      } catch (err) {
        console.error("API error:", err);
        this.error = "Không thể tải danh sách số điện thoại.";
        this.phoneData = [];
        this.originalPhoneData = [];
      } finally {
        this.loading = false;
      }
    },

    goToPhonePage(page) {
      if (page >= 1 && page <= this.phoneTotalPages) {
        this.phoneCurrentPage = page;
        this.fetchFileDetails(this.selectedFileId);
      }
    },

    // ========== Import Progress Polling ==========
    startImportPolling(fileId, fileName, totalRecords) {
      this.importProgress = {
        show: true,
        fileId: fileId,
        fileName: fileName,
        totalRecords: totalRecords,
        currentCount: 0,
        percent: 0
      };

      // Poll mỗi 1 giây
      this.importPollingInterval = setInterval(async () => {
        await this.checkImportProgress(fileId, totalRecords);
      }, 1000);
    },

    async checkImportProgress(fileId, totalRecords) {
      try {
        const res = await useJwt.get(`p/transactions/import-progress/${fileId}`, {
          params: { total: totalRecords }
        });
        
        if (res.data && res.data.code === 200 && res.data.result) {
          const data = res.data.result;
          
          this.importProgress.currentCount = data.current_count;
          this.importProgress.percent = data.percent || 0;

          // Hoàn thành -> dừng polling
          if (data.completed) {
            this.stopImportPolling();
            this.importProgress.percent = 100;
            
            setTimeout(() => {
              alert(`Import hoàn tất!\n\nFile: ${this.importProgress.fileName}\nĐã import: ${data.current_count.toLocaleString()} số`);
              this.importProgress.show = false;
              this.fetchFiles();
            }, 500);
          }
        }
      } catch (err) {
        console.error('Error polling import progress:', err);
      }
    },

    stopImportPolling() {
      if (this.importPollingInterval) {
        clearInterval(this.importPollingInterval);
        this.importPollingInterval = null;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },

    truncateNote(note, maxLength = 30) {
      if (!note) return '';
      if (note.length <= maxLength) return note;
      return note.substring(0, maxLength) + '...';
    },

    showNoteModal(note) {
      this.noteDetailContent = note;
      this.showNoteDetailModal = true;
    },

    closeNoteModal() {
      this.showNoteDetailModal = false;
      this.noteDetailContent = '';
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return '';

      // Nếu số bắt đầu bằng 84, chuyển thành 0
      if (phoneNumber.startsWith('84')) {
        phoneNumber = '0' + phoneNumber.substring(2);
      }

      // Format số điện thoại với dấu gạch ngang
      if (phoneNumber.charAt(0) === '0') {
        return phoneNumber.replace(/(\d{4})(\d{3})(\d{3})/, "$1$2$3");
      }

      return phoneNumber;
    },

    formatStatus(status) {
      if (!status) return '';
      const statusMap = {
        'PENDING': 'Chưa CNTT',
        'LOCK_2C': 'Khóa 2C',
        'LOCK_1C': 'Khóa 1C',
        'UPDATED': 'Đã CNTT',
        'SKIP': 'Bỏ qua',
        'REPROCESS': 'Thu hồi',
        'STOP': 'Đã dừng'
      };
      return statusMap[status] || status;
    },

    getStatusClass(status) {
      if (!status) return '';

      // Check for success indicators
      const successKeywords = ['SUCCESS', 'OK', 'COMPLETED', 'THÀNH CÔNG', '200', 'DELIVERED'];
      const failureKeywords = ['ERROR', 'FAIL', 'FAILED', 'THẤT BẠI', '500', '400', 'TIMEOUT', 'REJECTED'];

      const upperStatus = status.toUpperCase();

      // Check for success
      if (successKeywords.some(keyword => upperStatus.includes(keyword))) {
        return 'status-success';
      }

      // Check for failure
      if (failureKeywords.some(keyword => upperStatus.includes(keyword))) {
        return 'status-failure';
      }

      // Check for pending/processing
      if (['PENDING', 'PROCESSING', 'IN PROGRESS'].some(keyword => upperStatus.includes(keyword))) {
        return 'status-pending';
      }

      // Default status
      return 'status-default';
    },



    getVietnameseStatus(status) {
      if (!status) return '';

      const upperStatus = status.toUpperCase();

      // Success cases
      if (upperStatus.includes('SUCCESS') || upperStatus.includes('OK') || upperStatus.includes('200') || upperStatus.includes('COMPLETED') || upperStatus.includes('DELIVERED')) {
        return 'Thành công';
      }

      // Failure cases
      if (upperStatus.includes('ERROR') || upperStatus.includes('FAIL') || upperStatus.includes('FAILED') || upperStatus.includes('THẤT BẠI') || upperStatus.includes('500') || upperStatus.includes('400') || upperStatus.includes('TIMEOUT') || upperStatus.includes('REJECTED')) {
        return 'Thất bại';
      }

      // Pending cases
      if (upperStatus.includes('PENDING') || upperStatus.includes('PROCESSING') || upperStatus.includes('IN PROGRESS')) {
        return 'Đang xử lý';
      }

      // Return original status if no match
      return status;
    },

    // Search all phones methods
    switchToSearchTab() {
      this.activeTab = 'search';
      if (this.searchResults.length === 0) {
        this.searchAllPhones();
      }
    },

    async searchAllPhones(resetPage = true) {
      if (resetPage) {
        this.searchCurrentPage = 1;
      }
      this.searchLoading = true;
      try {
        const res = await useJwt.get("p/transactions/search_all_phones", {
          params: {
            page: this.searchCurrentPage,
            per_page: this.searchPerPage,
            phone: this.searchAllPhone,
            status: this.searchStatus
          }
        });
        // Response có wrapper: res.data.result chứa data thực
        const data = res.data?.result || res.data;
        if (data && data.code === 200) {
          this.searchResults = data.items || [];
          this.searchTotal = data.total || 0;
          this.searchTotalPages = data.total_pages || 0;
        }
      } catch (err) {
        console.error('Error searching phones:', err);
      } finally {
        this.searchLoading = false;
      }
    },

    goToSearchPage(page) {
      if (page < 1 || page > this.searchTotalPages) return;
      this.searchCurrentPage = page;
      this.searchAllPhones(false);
    },

    getStatusText(status) {
      const statusMap = {
        'PENDING': 'Chờ xử lý',
        'LOCK_1C': 'Khóa 1C',
        'LOCK_2C': 'Khóa 2C',
        'UPDATED': 'Đã cập nhật',
        'SKIP': 'Bỏ qua',
        'REPROCESS': 'Thu hồi',
        'STOP': 'Đã dừng'
      };
      return statusMap[status] || status;
    },

    getStatusBadgeClass(status) {
      const classMap = {
        'PENDING': 'badge bg-warning text-dark',
        'LOCK_1C': 'badge bg-danger',
        'LOCK_2C': 'badge bg-dark',
        'UPDATED': 'badge bg-success',
        'SKIP': 'badge bg-info',
        'REPROCESS': 'badge bg-secondary',
        'STOP': 'badge bg-secondary'
      };
      return classMap[status] || 'badge bg-secondary';
    },

    async stopSinglePhone(item) {
      if (this.stopLoadingPhoneId === item.id) return;
      
      const phoneDisplay = this.formatPhoneNumber(item.sdt);
      if (!confirm(`Bạn có chắc chắn muốn dừng xử lý số ${phoneDisplay} không?`)) {
        return;
      }
      
      this.stopLoadingPhoneId = item.id;
      try {
        const res = await useJwt.put("p/transactions/stop_phone", {
          phone_id: item.id,
          sdt: item.sdt,
          username: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'unknown',
        });
        
        if (res.data && res.data.code === 200) {
          const result = res.data.result;
          if (typeof result === 'object' && (result.code === 404 || result.code === 400)) {
            alert(result.str);
          } else if (typeof result === 'string') {
            alert(result);
            await this.searchAllPhones(false);
          } else {
            alert('Dừng xử lý số thành công!');
            await this.searchAllPhones(false);
          }
        } else {
          alert(res.data?.message || 'Lỗi khi dừng xử lý số.');
        }
      } catch (err) {
        console.error('Stop phone error:', err);
        if (err.response && err.response.data && typeof err.response.data.result === 'string') {
          alert(err.response.data.result);
        } else {
          alert('Không thể dừng xử lý số điện thoại.');
        }
      } finally {
        this.stopLoadingPhoneId = null;
      }
    },

    async stopDetailPhone(row) {
      if (this.stopLoadingPhoneId === row.id) return;
      
      const phoneDisplay = this.formatPhoneNumber(row.sdt);
      if (!confirm(`Bạn có chắc chắn muốn dừng xử lý số ${phoneDisplay} không?`)) {
        return;
      }
      
      this.stopLoadingPhoneId = row.id;
      try {
        const res = await useJwt.put("p/transactions/stop_phone", {
          phone_id: row.id,
          sdt: row.sdt,
          username: localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'unknown',
        });
        
        if (res.data && res.data.code === 200) {
          const result = res.data.result;
          if (typeof result === 'object' && (result.code === 404 || result.code === 400)) {
            alert(result.str);
          } else {
            alert(typeof result === 'string' ? result : 'Dừng xử lý số thành công!');
            await this.fetchFileDetails(this.selectedFileId);
          }
        } else {
          alert(res.data?.message || 'Lỗi khi dừng xử lý số.');
        }
      } catch (err) {
        console.error('Stop phone error:', err);
        alert(err.response?.data?.result || 'Không thể dừng xử lý số điện thoại.');
      } finally {
        this.stopLoadingPhoneId = null;
      }
    },

    viewPhoneHistory(item) {
      this.selectedPhoneNumber = item.sdt;
      this.showHistoryModal = true;
      this.fetchHistory(item.id);
    },

    openDetail(item) {
      this.selectedFile = item.file_name
      this.selectedFileId = item.file_id
      this.selectedFileStatus = item.status || null
      this.selectedImportBy = item.import_by || null
      this.selectedImportDate = item.import_date || null
      this.selectedRunDate = item.run_date || null
      this.phoneCurrentPage = 1
      this.showDetail = true
      this.fetchFileDetails(item.file_id)
    },
    backToList() {
      this.showDetail = false
      this.selectedFile = null
      this.selectedFileId = null
      this.selectedFileStatus = null
      this.selectedImportBy = null
      this.selectedImportDate = null
      this.selectedRunDate = null
      this.phoneData = []
      this.originalPhoneData = []
      this.searchPhone = ''
      this.phoneCurrentPage = 1
      this.phoneTotalItems = 0
      this.phoneTotalPages = 0
    },
    searchPhoneNumber() {
      const keyword = this.searchPhone ? this.searchPhone.trim() : '';
      this.phoneCurrentPage = 1;
      this.fetchFileDetails(this.selectedFileId, keyword);
    },
    resetSearch() {
      this.searchPhone = '';
      this.phoneCurrentPage = 1;
      this.fetchFileDetails(this.selectedFileId);
    },
    closeModal(event) {
      if (event.target.classList.contains('modal-overlay') || event.target.classList.contains('btn-close') || event.target.closest('.btn-close')) {
        this.showImportModal = false
        this.resetImportForm();
      }
    },

    handleDateChange(event) {
      const selectedDate = event.target.value;
      console.log('Selected date:', selectedDate);

      // selectedDate đã ở format yyyy-mm-dd từ input type="date"
      if (selectedDate) {
        const today = new Date();
        const selected = new Date(selectedDate);

        today.setHours(0, 0, 0, 0);
        selected.setHours(0, 0, 0, 0);

        if (selected < today) {
          alert('Không thể chọn ngày trong quá khứ. Vui lòng chọn ngày từ hôm nay trở đi.');
          this.importForm.startDate = '';
          return;
        }
      }

      this.importForm.startDate = selectedDate;
    },

    handleDateInput(event) {
      let value = event.target.value.replace(/\D/g, ''); // Chỉ giữ lại số

      // Format tự động khi nhập
      if (value.length >= 2) {
        value = value.substring(0, 2) + '/' + value.substring(2);
      }
      if (value.length >= 5) {
        value = value.substring(0, 5) + '/' + value.substring(5, 9);
      }

      // Cập nhật giá trị
      event.target.value = value;

      // Validate và set date
      if (value.length === 10) {
        const parts = value.split('/');
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);

        if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
          const selectedDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (selectedDate.getDate() === day && selectedDate.getMonth() === month - 1) {
            if (selectedDate < today) {
              alert('Không thể chọn ngày trong quá khứ. Vui lòng chọn ngày từ hôm nay trở đi.');
              event.target.value = '';
              this.importForm.startDate = '';
              return;
            }

            this.importForm.startDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }
      } else {
        this.importForm.startDate = '';
      }
    },

    showDatePicker() {
      // Khi focus vào input text, mở calendar
      this.openCalendar();
    },

    openCalendar() {
      // Trigger click vào input date ẩn để mở calendar
      this.$refs.hiddenDateInput.showPicker();
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFile = file;
        this.uploadedFileName = file.name;
      }
    },

    readFileContent(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          let phoneNumbers = [];

          if (file.name.endsWith('.csv')) {
            // Xử lý file CSV
            const text = e.target.result;
            const lines = text.split('\n');

            // Bỏ qua dòng đầu tiên (header), bắt đầu từ dòng thứ 2
            for (let i = 1; i < lines.length; i++) {
              const line = lines[i].trim();
              if (line) {
                const columns = line.split(',');
                const phoneNumber = columns[0].replace(/['"]/g, '').trim(); // Lấy cột đầu tiên, bỏ dấu ngoặc kép
                // Chỉ bỏ qua dòng trống, GỬI TẤT CẢ số lên backend để validate
                if (phoneNumber) {
                  phoneNumbers.push(phoneNumber);
                }
              }
            }
          } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
            // Xử lý file Excel
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            // Bỏ qua dòng đầu tiên (header), bắt đầu từ dòng thứ 2
            for (let i = 1; i < jsonData.length; i++) {
              const row = jsonData[i];
              if (row && row[0]) {
                const phoneNumber = String(row[0]).trim();
                // Chỉ bỏ qua dòng trống, GỬI TẤT CẢ số lên backend để validate
                if (phoneNumber) {
                  phoneNumbers.push(phoneNumber);
                }
              }
            }
          }

          this.phoneNumbersList = phoneNumbers;

        } catch (error) {
          console.error('Error reading file:', error);
          alert('Lỗi khi đọc file. Vui lòng kiểm tra định dạng file.');
        } finally {
          this.fileReading = false; // Stop loading
        }
      };

      reader.onerror = (error) => {
        console.error('File reading error:', error);
        alert('Lỗi khi đọc file.');
        this.fileReading = false; // Stop loading on error
      };

      // Đọc file theo định dạng phù hợp
      if (file.name.endsWith('.csv')) {
        reader.readAsText(file, 'UTF-8');
      } else {
        reader.readAsArrayBuffer(file);
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    clearFile() {
      this.uploadedFile = null;
      this.uploadedFileName = '';
      this.phoneNumbersList = [];
      this.fileReading = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async handleImport() {
      if (!this.uploadedFile) {
        alert('Vui lòng chọn file để import!');
        return;
      }

      if (!this.importForm.startDate) {
        alert('Vui lòng chọn ngày bắt đầu!');
        return;
      }

      // Kiểm tra ngày chọn không được trong quá khứ
      const selectedDate = new Date(this.importForm.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        alert('Không thể chọn ngày trong quá khứ. Vui lòng chọn ngày từ hôm nay trở đi.');
        return;
      }

      this.loading = true;

      try {
        // Upload file trực tiếp qua Java để bypass WAF
        // Java sẽ đọc file và forward sang Python
        console.log('[Import] Using file upload to bypass WAF');
        
        const formData = new FormData();
        formData.append('file', this.uploadedFile);
        formData.append('run_date', this.importForm.startDate);
        formData.append('username', localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).username : 'unknown');
        formData.append('note', this.importForm.notes || '');
        
        // Show progress
        this.importProgress = {
          show: true,
          fileId: null,
          fileName: `Đang upload: ${this.uploadedFile.name}`,
          totalRecords: 0,
          currentCount: 0,
          percent: 0
        };

        const response = await useJwt.axiosIns.post($ctx.API_URL + 'p/transactions/import-file', formData, {
          headers: {
            'apiKey': localStorage.getItem('accessToken')
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.importProgress.percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              this.importProgress.currentCount = progressEvent.loaded;
            }
          }
        });

        console.log('Import response:', response.data);
        await this.handleImportResponse(response);

      } catch (error) {
        console.error('Upload error:', error);
        
        // Tắt modal progress khi có lỗi
        this.importProgress.show = false;

        // Xử lý lỗi từ catch block (network error, timeout, etc.)
        if (error.response && error.response.data) {
          const errorData = error.response.data;

          if (errorData.result && errorData.result.str) {
            alert(`Lỗi: ${errorData.result.str}`);
          } else if (errorData.message) {
            alert(`Lỗi: ${errorData.message}`);
          } else {
            alert('Lỗi khi upload file: ' + error.message);
          }
        } else {
          alert('Lỗi khi upload file: ' + error.message);
        }
      } finally {
        this.loading = false;
      }
    },

    async uploadChunked(chunkSize) {
      const totalPhones = this.phoneNumbersList.length;
      const totalChunks = Math.ceil(totalPhones / chunkSize);
      const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const fileName = this.uploadedFile.name.replace(/\.[^/.]+$/, "");
      
      // Show progress overlay
      this.importProgress = {
        show: true,
        fileId: uploadId,
        fileName: `Đang upload: ${fileName}`,
        totalRecords: totalPhones,
        currentCount: 0,
        percent: 0
      };
      
      console.log(`Starting chunked upload: ${totalPhones} phones in ${totalChunks} chunks`);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, totalPhones);
        const phoneChunk = this.phoneNumbersList.slice(start, end);
        
        // Update progress
        this.importProgress.currentCount = end;
        this.importProgress.percent = (end / totalPhones) * 100;
        this.importProgress.fileName = `Đang upload chunk ${i + 1}/${totalChunks}: ${fileName}`;
        
        const payload = {
          upload_id: uploadId,
          chunk_index: i,
          total_chunks: totalChunks,
          phone: phoneChunk
        };
        
        // Include metadata in first chunk
        if (i === 0) {
          payload.file_name = fileName;
          payload.run_date = this.importForm.startDate;
          payload.username = localStorage.getItem('userData') 
            ? JSON.parse(localStorage.getItem('userData')).username 
            : 'unknown';
          payload.note = this.importForm.notes || '';
        }
        
        console.log(`Uploading chunk ${i + 1}/${totalChunks} (${phoneChunk.length} phones)`);
        
        try {
          const response = await useJwt.post('p/transactions/import-chunk', payload, {
            headers: { 'Content-Type': 'application/json' }
          });
          
          // Check for errors in intermediate chunks
          if (response.data && response.data.code !== 200) {
            this.importProgress.show = false;
            alert(`Lỗi khi upload chunk ${i + 1}: ${response.data.str || 'Unknown error'}`);
            return null;
          }
          
          // Last chunk returns the final response
          if (i === totalChunks - 1) {
            this.importProgress.percent = 100;
            this.importProgress.fileName = `Upload hoàn tất, đang xử lý: ${fileName}`;
            // Don't hide progress - let handleImportResponse continue with polling
            return response;
          }
        } catch (error) {
          console.error(`Chunk ${i + 1} upload error:`, error);
          this.importProgress.show = false;
          alert(`Lỗi khi upload chunk ${i + 1}/${totalChunks}: ${error.message}`);
          return null;
        }
      }
      
      this.importProgress.show = false;
      return null;
    },

    async handleImportResponse(response) {
      // Hide progress overlay first
      this.importProgress.show = false;
      
      // Handle direct error response (code: 400 at top level)
      if (response.data && response.data.code === 400) {
        alert(`Lỗi: ${response.data.str || 'Đã xảy ra lỗi'}`);
        this.resetImportForm();
        return;
      }
      
      // Backend luôn trả về code: 200 ở ngoài
      if (response.data && response.data.code === 200) {
        const result = response.data.result;

        // Case: Background task - 202 Accepted
        if (result.code === 202) {
          // Hiện thông báo nếu có số bị skip
          if (result.skipped_existing > 0) {
            alert(`${result.message}\n\nSố đã tồn tại sẽ được bỏ qua.`);
          }
          // Continue with polling - keep progress visible, update with new file_id
          this.startImportPolling(result.file_id, result.file_name, result.total_records);
          this.resetImportForm();
          this.showImportModal = false;
        }
        // Kiểm tra result.code để xác định success hay error
        else if (result.code === 200) {
          // SUCCESS - Import thành công
          alert(`Thành công: ${result.str}\nSố lượng: ${result.total_imported} số điện thoại`);
          await this.fetchFiles(); // Refresh danh sách file
          this.resetImportForm();
          this.showImportModal = false;
        }
        else if (result.code === 400) {

          if (result.str === "Có số điện thoại bị trùng trong file") {
            // Download file TRƯỚC khi hiện alert
            this.downloadImportFileWithComments(
              result.form,              // Danh sách tất cả số trong file
              result.phoneDuplicate,    // Danh sách số bị trùng
              this.uploadedFile.name,
              'Trùng lặp trong file'
            );

            alert(`Lỗi: ${result.str}\n\nFile báo cáo đã được tải xuống với danh sách số trùng lặp.`);
            this.resetImportForm();
          }

          else if (result.str === "Có số điện thoại không hợp lệ") {
            // Download file TRƯỚC khi hiện alert
            this.downloadImportFileWithComments(
              result.form,              // Danh sách tất cả số trong file
              result.phoneInvalid,        // Danh sách số không hợp lệ
              this.uploadedFile.name,
              'Không hợp lệ'
            );

            alert(`Lỗi: ${result.str}\nFile báo cáo đã được tải xuống với danh sách số không hợp lệ.`);
            this.resetImportForm();
          }
          // Case 3: File CSV không có dữ liệu
          else if (result.str === "File CSV không có dữ liệu hợp lệ") {
            alert(`Lỗi: ${result.str}`);
            this.resetImportForm();
          }
          // Case 4: Tất cả số đã tồn tại với cùng ngày
          else if (result.str && result.str.includes("Tất cả số điện thoại đã tồn tại")) {
            alert(`Lỗi: ${result.str}\n\nTổng số: ${result.total_existing} số điện thoại`);
            this.resetImportForm();
          }
          // Case 5: Lỗi khác từ backend
          else {
            alert(`Lỗi: ${result.str || 'Đã xảy ra lỗi không xác định'}`);
            this.resetImportForm();
          }
        }
        else if (result.code === 500) {
          // SERVER ERROR
          alert(`Lỗi server: ${result.str || 'Đã xảy ra lỗi không xác định'}`);
          this.resetImportForm();
        }
        else {
          // Trường hợp result.code khác
          console.error('Unexpected result code:', result);
          alert(`Lỗi: ${result.str || 'Response không xác định'}`);
          this.resetImportForm();
        }
      }
      else {
        // Trường hợp response.data.code khác 200
        console.error('Unexpected response code:', response.data);
        alert('Lỗi: Response từ server không đúng định dạng');
      }
    },

    resetImportForm() {
      this.uploadedFile = null;
      this.uploadedFileName = '';
      this.phoneNumbersList = [];
      this.importForm.startDate = '';
      this.importForm.notes = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    downloadImportFileWithComments(formData, phoneExistData, originalFileName, comment1) {
      if (!formData || formData.length === 0) {
        console.log('Không có dữ liệu form để tạo file');
        return;
      }

      // Tạo dữ liệu cho worksheet với header có cột chú thích
      const wsData = [['Số thuê bao', 'Chú thích']];

      // Chuẩn hóa danh sách số lỗi để so sánh (loại bỏ khoảng trắng, newline, etc.)
      const errorPhones = phoneExistData ? phoneExistData.map(p => {
        const normalized = String(p).trim().replace(/\s+/g, '');
        console.log(`Normalizing error phone: "${p}" -> "${normalized}"`);
        return normalized;
      }) : [];

      console.log('Error phones normalized:', errorPhones);

      // Duyệt qua từng số trong form và thêm chú thích
      formData.forEach((item, index) => {
        let comment = '';
        const normalizedItem = String(item).trim().replace(/\s+/g, '');

        console.log(`[${index}] Checking: "${item}" -> normalized: "${normalizedItem}"`);

        // Kiểm tra xem số này có trong danh sách lỗi không
        const isError = errorPhones.includes(normalizedItem);
        console.log(`  -> In error list? ${isError}`);

        if (isError) {
          comment = comment1;
          console.log(`  -> ✅ MATCH! Adding comment: "${comment1}"`);
        }

        // Thêm dòng dữ liệu
        wsData.push([normalizedItem, comment]);
      });

      console.log('Final worksheet data:', wsData);
      console.log('=== DEBUG END ===');

      // Tạo worksheet từ dữ liệu
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // Thiết lập độ rộng cột
      if (!ws['!cols']) ws['!cols'] = [];
      ws['!cols'][0] = { width: 15 }; // Cột số thuê bao
      ws['!cols'][1] = { width: 25 }; // Cột chú thích

      // Tạo workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Import Result');

      // Tạo tên file
      const downloadFileName = `${originalFileName.replace(/\.[^/.]+$/, '')}_phone_report.xlsx`;

      // Tải file Excel
      XLSX.writeFile(wb, downloadFileName);

      console.log(`File đã tải xuống: ${downloadFileName}`);
    },
    downloadTemplate() {
      const wb = XLSX.utils.book_new();
      const wsData = [
        ['Số thuê bao'],
        ['8487xxx xxx']
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      if (!ws['!cols']) ws['!cols'] = [];
      ws['!cols'][0] = { width: 15 };

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      const fileName = `File_Import_demo.xlsx`;

      XLSX.writeFile(wb, fileName);

    },
    createExcelWithTimesNewRoman(data, fileName) {
      const wsData = [['Số thuê bao']];

      data.forEach(item => {
        let phoneNumber = item.sdt;
        if (phoneNumber && phoneNumber.startsWith('0')) {
          phoneNumber = '84' + phoneNumber.substring(1);
        }
        wsData.push([phoneNumber]);
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      if (!ws['!cols']) ws['!cols'] = [];
      ws['!cols'][0] = { width: 15 };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Phone Numbers');

      XLSX.writeFile(wb, fileName);
      console.log(`Downloaded template file: ${fileName}`);
    },

    // History modal methods
    showHistory(row) {
      this.selectedPhoneNumber = row.sdt;
      this.showHistoryModal = true;
      this.fetchHistory(row.id);
    },

    closeHistoryModal() {
      this.showHistoryModal = false;
      this.selectedPhoneNumber = '';
      this.historyData = [];
      this.historyError = null;
    },

    async fetchHistory(phoneCheckInfoId) {
      this.loadingHistory = true;
      this.historyError = null;

      try {
        const res = await useJwt.get("p/transactions/get_by_phone", {
          params: { phone_check_info_id: phoneCheckInfoId }
        });

        if (res.data && res.data.code === 200) {
          // Handle different response structures
          let data = null;

          // Check for nested structure (result.result)
          if (res.data.result && res.data.result.code === 200 && res.data.result.result) {
            data = res.data.result.result;
          }
          // Check for simple structure (result is array)
          else if (Array.isArray(res.data.result)) {
            data = res.data.result;
          }
          // Check for direct array structure
          else if (Array.isArray(res.data)) {
            data = res.data;
          }

          if (data && Array.isArray(data)) {
            // Sort by action_time descending
            this.historyData = data.sort((a, b) => new Date(b.action_time) - new Date(a.action_time));
          } else {
            this.historyData = [];
            console.warn("Unexpected response structure:", res.data);
          }
        } else {
          this.historyError = res.data.message || "Không thể tải lịch sử.";
          this.historyData = [];
        }
      } catch (err) {
        console.error("Error fetching history:", err);
        this.historyError = "Không thể tải lịch sử. Vui lòng thử lại sau.";
        this.historyData = [];
      } finally {
        this.loadingHistory = false;
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },

    formatType(type) {
      const typeMap = {
        'SMS_1C': 'SMS 1C',
        'SMS_2C': 'SMS 2C',
        'LOCK_1C': 'Khóa 1C',
        'LOCK_2C': 'Khóa 2C',
        'UNLOCK_1C': 'Mở khóa 1C',
        'UNLOCK_2C': 'Mở khóa 2C',
        'REPROCESS': 'Thu hồi',
        'CHECK_TRANS': 'Kiểm tra GD',
        'OTHER': 'Khác'
      };
      return typeMap[type] || type;
    },

    getTypeBadgeClass(type) {
      const classMap = {
        'SMS_1C': 'status-success',
        'SMS_2C': 'status-success',
        'LOCK_1C': 'status-pending',
        'LOCK_2C': 'status-failure',
        'UNLOCK_1C': 'status-success',
        'UNLOCK_2C': 'status-success',
        'REPROCESS': 'status-default',
        'CHECK_TRANS': 'status-pending',
        'OTHER': 'status-default'
      };
      return classMap[type] || 'status-default';
    },

    isSMSType(type) {
      return ['SMS_1C', 'SMS_2C'].includes(type);
    },

    // Content detail modal methods
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    showContentDetail(item) {
      this.contentDetailData = item;
      this.contentDetailTitle = `Chi tiết: ${this.formatType(item.type)} - ${item.sdt}`;
      this.showContentDetailModal = true;
    },

    closeContentDetailModal() {
      this.showContentDetailModal = false;
      this.contentDetailData = {};
      this.contentDetailTitle = '';
    },

    async copySmsContent() {
      const item = this.contentDetailData;
      const contentToCopy = item.sms_content || '';

      try {
        await navigator.clipboard.writeText(contentToCopy);
        alert('Đã sao chép nội dung SMS vào clipboard!');
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = contentToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          alert('Đã sao chép nội dung SMS vào clipboard!');
        } catch (err) {
          alert('Không thể sao chép. Vui lòng sao chép thủ công.');
        }
        document.body.removeChild(textArea);
      }
    }
  }
}
</script>


<style scoped>
.bg-white {
  background-color: #ffffff !important;
}

.min-vh-100 {
  min-height: 100vh;
}

.table th {
  background-color: #696969;
  font-weight: 600;
  border-color: #dee2e6;
}

.table td {
  vertical-align: middle;
  border-color: #dee2e6;
}

.table-hover tbody tr:hover {
  background-color: #f5f5f5;
}

.text-center {
  color: #000000;
  text-transform: none;
}

.text-decoration-underline {
  text-decoration: underline;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

.rounded {
  border-radius: 0.375rem !important;
}

/* popup import */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #000;
}

.modal-title {
  font-weight: 600;
  color: #333;
}

.form-label {
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
}

.input-group-text {
  background-color: #f8f9fa;
  border-left: none;
}

.btn-outline-secondary {
  border: 2px dashed #dee2e6;
  color: #6c757d;
  background-color: #f8f9fa;
}

.btn-outline-secondary:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

/* Sửa lỗi tên file bị tràn */
.upload-container {
  position: relative;
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  padding: 1rem !important;
  gap: 4px;
  min-height: 52px;
  transition: all 0.2s ease;
  width: 100%;
}

/* Khi chưa có file - text ở giữa, inline */
.upload-btn:not(.has-file) {
  justify-content: center;
  flex-direction: row;
}

/* Khi đã có file - text xuống dòng, hiển thị full */
.upload-btn.has-file {
  flex-direction: column;
  justify-content: center;
  padding: 0.75rem 2.5rem 0.75rem 1rem !important;
  min-height: auto;
}

.upload-btn i {
  flex-shrink: 0;
  font-size: 16px;
  margin-right: 0;
}

.file-name-text {
  flex: 0 1 auto;
  min-width: 0;
}

/* Khi chưa có file - text inline, căn giữa */
.upload-btn:not(.has-file) .file-name-text {
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Khi đã có file - text xuống nhiều dòng, hiển thị full */
.upload-btn.has-file .file-name-text {
  text-align: center;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-all;
  line-height: 1.4;
  width: 100%;
}

/* Nút xóa file */
.btn-clear-file {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background-color: transparent;
  color: #6c757d;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  padding: 0;
  font-size: 16px;
  outline: none;
}

.btn-clear-file:hover {
  background-color: rgba(108, 117, 125, 0.1);
  color: #495057;
  transform: scale(1.15);
}

.btn-clear-file:active {
  transform: scale(0.95);
}

.btn-clear-file i {
  margin: 0;
}

.file-info-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  font-size: 13px;
}

/* Styling cho cột Thao tác */
.action-cell {
  padding: 0.5rem !important;
}

.action-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.action-link {
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.action-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

.action-link.disabled {
  color: #6c757d;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.7;
}

/* Note link styles */
.note-link {
  color: #007bff;
  cursor: pointer;
  max-width: 150px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* History modal styles */
.history-modal {
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
}

.history-container {
  max-height: 400px;
  overflow-y: auto;
}


/* Table styles for history */
.table-sm th,
.table-sm td {
  padding: 0.5rem;
  vertical-align: middle;
}

/* SMS content styling */
.sms-content {
  font-style: italic;
  color: #6c757d;
  max-width: none;
  word-wrap: break-word;
  white-space: pre-wrap;
  display: block;
  text-align: left;
  line-height: 1.4;
  font-size: 0.9rem;
}

/* Response content styling */
.response-content {
  font-size: 0.85rem;
  word-break: break-all;
  max-width: 250px;
  line-height: 1.3;
}

/* Content clickable styling */
.sms-content-truncate {
  cursor: pointer;
  color: #007bff;
  transition: color 0.2s ease;
  display: inline-block;
  max-width: 100%;
  word-wrap: break-word;
  font-style: italic;
  font-size: 0.9rem;
  line-height: 1.4;
}

.sms-content-truncate:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* Content detail modal styles */
.content-detail-modal {
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
}

.content-detail-info {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.content-section {
  margin-bottom: 20px;
}

.content-display {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #333;
}

.sms-display {
  background-color: #e9ecef;
  border-left: 4px solid #17a2b8;
}


/* Status badge styling */
.status-badge {
  padding: 0.35em 0.65em;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.25rem;
  display: inline-block;
  min-width: 80px;
  text-align: center;
}

.status-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-success::before {
  content: '';
}

.status-failure {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status-failure::before {
  content: '';
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-pending::before {
  content: '';
}

.status-default {
  background-color: #e2e3e5;
  color: #383d41;
  border: 1px solid #ced4da;
}

/* Import Progress Overlay */
.import-progress-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.import-progress-modal {
  background: white;
  padding: 30px 40px;
  border-radius: 12px;
  min-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
</style>
